/**
 * UpSurv 2.0 — the validation engine (specification §4).
 *
 * Eleven rules, one visibility evaluator, and gap detection. Every function
 * here is pure: no clock, no randomness, no I/O. Same document in, byte-
 * identical issues out — which is what lets the live validation chip, the
 * submit gate, and the fixture round-trip all agree with each other.
 *
 * ── Three things this module gets right on purpose ───────────────────────────
 *
 *  1. **Gates resolve at the level of the REFERENCED question**, via
 *     `conditionScope()` from catalog.ts — never by rebuilding the key in the
 *     asking scope. L4 is a per-group lobby question gated on B8, a *building*
 *     answer; asking for "room:r1|grp:gA|B8" reads a key that can never exist
 *     and silently hides L4/L4a on every consultant job. `isVisible` is the one
 *     door every gate walks through so that mistake has one place to live.
 *
 *  2. **Fully-derived questions are never gaps.** `DERIVED_ONLY_QUESTION_IDS`
 *     (B14, B17, E4b, E4c, E6a, E9d, F1, L1) are computed from structure — the
 *     surveyor supplies nothing — so "unanswered" is not a state they have.
 *     Treating them as unanswered would flag a gap on every clean survey.
 *     Structural ids (M1/M2/M3/M6/M7) are excluded for the mirror-image reason:
 *     their values live in the entity tree, not in `answers`.
 *
 *  3. **Rules 2 and 6 have different shapes.** Disconnects are exact equality —
 *     "leave as-is" is not a disposition for a disconnect. Pit ladders are a
 *     ceiling — the remainder is code-compliant and stays as it is. Do not
 *     "unify" them.
 *
 * ── Severity, and what actually stops a survey ───────────────────────────────
 * `error` becomes a gap; `warning` and `info` do not. That is how §4 rules 8
 * (missing photos "never block") and 5 (the flip fixes itself and explains) stay
 * out of the blocking path while still travelling with the record. Missing
 * photos DO become `missing_photo` gaps so the office sees them on the submitted
 * survey, but they are not in `BLOCKING_GAP_REASONS` — rule 8 says listed, rule
 * 11 says a gap on a *required answer* flags the survey.
 */

import {
  DERIVED_ONLY_QUESTION_IDS,
  STRUCTURAL_QUESTION_IDS,
  byId,
  conditionScope,
  questionsFor,
  visibleOptions,
} from './catalog';
import { SHOT_CONDITIONS, buildSurveySections, photoCoversShot, qualifyTitle, shotBaseId } from './sections';
import { answerKey, formSetFor, sectionInstanceId } from './types';
import type {
  AnswerValue,
  AutoFix,
  Gap,
  GapReason,
  IssueTally,
  MachineRoom,
  Note,
  QuestionCondition,
  QuestionDef,
  QuestionOption,
  Scope,
  SectionInstance,
  SubmittedSurvey,
  SurveyDocument,
  SurveyStatus,
  ValidationIssue,
} from './types';

/* ════════════════════════════ small readers ════════════════════════════ */

function roomOf(doc: SurveyDocument, scope: Scope): MachineRoom | undefined {
  return scope.roomId ? doc.rooms.find((r) => r.id === scope.roomId) : undefined;
}

/**
 * The scope an answer to `question` is stored under, narrowed from a section or
 * gate scope. Delegates to `conditionScope` so the narrowing rule lives in
 * exactly one place — a building question drops every segment, a group question
 * keeps room + group, and so on.
 */
function questionScope(question: QuestionDef, scope: Scope): Scope {
  return conditionScope({ questionId: question.id }, scope);
}

function answerOf(doc: SurveyDocument, questionId: string, scope: Scope): AnswerValue | undefined {
  const question = byId(questionId);
  const resolved = question ? questionScope(question, scope) : scope;
  return doc.answers[answerKey(questionId, resolved)];
}

/**
 * A "Y/N + count" read. A missing answer reads as zero so the live chip starts
 * at "0 + 0 = 0 — 6 cars unaccounted for" rather than staying silent until the
 * surveyor happens to touch both steppers.
 */
function countOf(doc: SurveyDocument, questionId: string, scope: Scope): number {
  const answer = answerOf(doc, questionId, scope);
  if (answer?.kind !== 'flagCount') return 0;
  return answer.value.flag ? answer.value.count : 0;
}

function boolOf(doc: SurveyDocument, questionId: string, scope: Scope): boolean | undefined {
  const answer = answerOf(doc, questionId, scope);
  return answer?.kind === 'bool' ? answer.value : undefined;
}

function selectOf(doc: SurveyDocument, questionId: string, scope: Scope): string | undefined {
  const answer = answerOf(doc, questionId, scope);
  return answer?.kind === 'select' ? answer.value : undefined;
}

function numberOf(doc: SurveyDocument, questionId: string, scope: Scope): number | undefined {
  const answer = answerOf(doc, questionId, scope);
  return answer?.kind === 'number' ? answer.value : undefined;
}

function pluralize(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/**
 * Whether a stored answer actually carries content. `0`, `false` and
 * `{flag:false,count:0}` are real answers; an empty string, an empty range list
 * or a measure with nothing measured are not — those are a control the surveyor
 * opened and left blank, which reads as unanswered (§4 rule 11).
 *
 * ── "Yes, zero of them" is not an answer either ──────────────────────────────
 * A `flagCount` sitting at `{flag:true, count:0}` is a CONTRADICTION, not a
 * blank and not a zero: the surveyor said there are some and then never said how
 * many, and the office cannot price it either way. It is the one state the
 * quantity control can be left in by accident — tapping Yes reveals the stepper
 * already reading 0 — so it is reported as unanswered and travels to the office
 * as a gap rather than being silently priced at nothing. `{flag:false,count:0}`
 * is untouched by this: "no, none" is complete and is what every one of these
 * questions opens on (D-016).
 */
/**
 * Answered, for THIS question (D-051). `isAnswered` says whether anything was
 * typed; a ZIP is answered only when all five digits are there — one digit is
 * a keystroke, not an answer, and the last look must not treat it as one.
 */
export function isAnsweredFor(question: QuestionDef, value: AnswerValue | undefined): boolean {
  if (!isAnswered(value)) return false;
  if (question.id === 'B2c' && value?.kind === 'text') return ZIP_PATTERN.test(value.value.trim());
  return true;
}

export function isAnswered(value: AnswerValue | undefined): boolean {
  if (!value) return false;
  switch (value.kind) {
    case 'text':
    case 'select':
    case 'date':
      return value.value.trim() !== '';
    case 'multiselect':
    case 'ranges':
      return value.value.length > 0;
    case 'measure':
      return (
        value.value.feet !== undefined ||
        value.value.lengthFt !== undefined ||
        value.value.widthFt !== undefined
      );
    case 'contact':
      return Boolean(value.value.name || value.value.phone || value.value.email);
    case 'flagCount':
      return !(value.value.flag && value.value.count === 0);
    case 'number':
    case 'bool':
    case 'tristate':
      return true;
  }
}

/* ═══════════════════════════ visibility (branching) ═══════════════════════ */

function answerEquals(answer: AnswerValue, expected: boolean | string | number): boolean {
  switch (answer.kind) {
    case 'bool':
    case 'number':
    case 'text':
    case 'select':
    case 'date':
    case 'tristate':
      return (answer.value as boolean | string | number) === expected;
    case 'multiselect':
      return typeof expected === 'string' && answer.value.includes(expected);
    case 'contact':
    case 'measure':
    case 'ranges':
    case 'flagCount':
      // Composite answers have no scalar to compare; no §3 gate uses one.
      return false;
  }
}

/**
 * Evaluate one `showWhen` condition.
 *
 * The referenced answer is resolved with `conditionScope`, NOT with the asking
 * question's scope — see the module header. `structural` predicates and the one
 * resolvable structural id (M1, room type) read the entity tree instead.
 */
function conditionHolds(cond: QuestionCondition, doc: SurveyDocument, scope: Scope): boolean {
  if (cond.structural) {
    const room = roomOf(doc, scope);
    if (!room) return false;
    const set = formSetFor(room.type);
    return cond.structural === 'roomTypeIsTraction' ? set === 'traction' : set === 'hydro';
  }

  if (cond.questionId === 'M1') {
    const room = roomOf(doc, scope);
    if (!room) return false;
    return cond.equals === undefined ? true : room.type === cond.equals;
  }
  if (STRUCTURAL_QUESTION_IDS.includes(cond.questionId)) {
    // M2/M3/M6/M7 hold names and lists, not gate values. Nothing in §3 gates on
    // one; a test in tests/unit/validation.test.ts keeps it that way.
    return false;
  }

  const refScope = conditionScope(cond, scope);
  const ref = byId(cond.questionId);

  // A gate is CLOSED when the question it references is not itself asked.
  //
  // The catalog has exactly one two-level chain, B8 → L4 → L4a, and L4a names
  // only L4 — it does not repeat the B8 gate the way E4b/E7b/H4 repeat theirs.
  // Without this check, a surveyor who answers L4 = Yes on a consultant job and
  // then corrects B8 to No leaves a stale L4 behind: L4 correctly disappears,
  // L4a does not, and the per-landing wall types stay visible and REQUIRED —
  // flagging a gap on a job that is not a consultant bid at all. That is the
  // same stale-parent hazard `ruleChilledWater` guards by hand for H1a; this is
  // that guard generalized, so no future catalog chain can reintroduce it.
  //
  // `refScope` is already narrowed to the referenced question's level, which is
  // the scope its own gates must be read in. The showWhen graph is a DAG — a
  // contract test in tests/unit/validation.test.ts keeps it that way — so this
  // mutual recursion with isVisible always terminates.
  if (ref && !isVisible(ref, doc, refScope)) return false;

  const answer = doc.answers[answerKey(cond.questionId, refScope)];
  if (!answer) return false;
  if (cond.flagIs !== undefined) {
    return answer.kind === 'flagCount' && answer.value.flag === cond.flagIs;
  }
  if (cond.equals === undefined) return isAnswered(answer);
  return answerEquals(answer, cond.equals);
}

/**
 * Is this question asked, in this scope, given what has been answered so far?
 *
 * Two filters, in order: the room's form set (G11–G17 vanish in a Hydro room;
 * E14 and P6 vanish in a Traction one — MRL rooms run the Hydro set), then
 * every `showWhen` condition. All conditions must hold.
 */
export function isVisible(question: QuestionDef, doc: SurveyDocument, scope: Scope = {}): boolean {
  const room = roomOf(doc, scope);
  if (question.formSets && room && !question.formSets.includes(formSetFor(room.type))) {
    return false;
  }
  for (const cond of question.showWhen ?? []) {
    if (!conditionHolds(cond, doc, scope)) return false;
  }
  return true;
}

/**
 * The choices to render for a question HERE — form set and per-option gate both
 * applied. **D-024.**
 *
 * `visibleOptions(question, formSet)` in catalog.ts narrows by form set (D-007)
 * and can do no more, because a per-option `showWhen` needs a document to read.
 * This is that same filter with the document supplied, and it runs the option's
 * gate through `conditionHolds` — the one evaluator every gate in the catalog
 * goes through, scope narrowing and stale-parent rule included.
 *
 * Today it decides exactly one thing: whether the machine-room relay inventory
 * (F8) offers a **Shunt** box, which it does only where there is a sprinkler
 * (M4 = Yes). Any screen rendering a select or multiselect should call this
 * rather than the catalog helper; the catalog helper drops conditional options
 * outright, so a caller that forgets loses the choice rather than inventing one.
 */
export function optionsFor(
  question: QuestionDef,
  doc: SurveyDocument,
  scope: Scope = {},
): QuestionOption[] {
  const asked = questionScope(question, scope);
  const room = roomOf(doc, asked);
  // Building and ad hoc questions have no room; no option at that level is
  // form-set tagged, so either set answers the same.
  const formSet = room ? formSetFor(room.type) : 'traction';
  return visibleOptions(question, formSet, (condition) =>
    conditionHolds(condition, doc, asked),
  );
}

/* ═════════════════════════════ issue helpers ═════════════════════════════ */

interface IssueSeed {
  ruleId: string;
  severity: ValidationIssue['severity'];
  scope: Scope;
  questionIds: string[];
  message: string;
  explanation?: string;
  autoFix?: AutoFix;
  tally?: IssueTally;
}

function issue(seed: IssueSeed): ValidationIssue {
  return {
    ruleId: seed.ruleId,
    severity: seed.severity,
    scope: seed.scope,
    questionIds: seed.questionIds,
    message: seed.message,
    ...(seed.explanation ? { explanation: seed.explanation } : {}),
    ...(seed.autoFix ? { autoFix: seed.autoFix } : {}),
    ...(seed.tally ? { tally: seed.tally } : {}),
  };
}

/* ═══════════════ V1 — building unit total vs. room car sums ═══════════════ */

/** §4 rule 1. B11 must equal the cars actually assigned across every room. */
function ruleUnitTotal(doc: SurveyDocument): ValidationIssue[] {
  const declared = numberOf(doc, 'B11', {});
  if (declared === undefined) return []; // unanswered is rule 11's business
  const covered = doc.rooms.reduce((n, room) => n + room.cars.length, 0);
  if (covered === declared) return [];

  const message =
    covered < declared
      ? `Your room surveys account for ${covered} of ${declared} elevators — ` +
        `${pluralize(declared - covered, 'car')} unaccounted for.`
      : `Your room surveys account for ${covered} elevators but the building total says ` +
        `${declared} — that's more cars than the building has.`;

  return [
    issue({
      ruleId: 'V1',
      severity: 'error',
      scope: {},
      questionIds: ['B11'],
      message,
      explanation:
        'Every car in the building belongs to exactly one machine room survey. ' +
        'Either the building total is wrong or a room is missing cars.',
      tally: {
        subject: 'Building total',
        running: covered,
        target: declared,
        unit: 'car',
        shape: 'exact',
      },
    }),
  ];
}

/* ═══════════ V2 / V3 — disconnect dispositions, exact, independent ════════ */

/**
 * §4 rules 2 and 3 share arithmetic and nothing else: each car has TWO
 * disconnects (a mainline and a cab light 110), so the two splits are checked
 * separately and neither one excuses the other.
 */
function disconnectSplit(
  doc: SurveyDocument,
  room: MachineRoom,
  ruleId: string,
  label: string,
  replaceId: string,
  refurbishId: string,
  explanation: string,
): ValidationIssue[] {
  const scope: Scope = { roomId: room.id };
  const cars = room.cars.length;
  const replace = countOf(doc, replaceId, scope);
  const refurbish = countOf(doc, refurbishId, scope);
  const sum = replace + refurbish;
  if (sum === cars) return [];

  const tail =
    sum < cars
      ? `${pluralize(cars - sum, 'car')} unaccounted for`
      : `that's more disconnects than cars`;

  return [
    issue({
      ruleId,
      severity: 'error',
      scope,
      questionIds: [replaceId, refurbishId],
      message: `${label}: ${replace} + ${refurbish} = ${sum} — ${tail}`,
      explanation,
      tally: { subject: label, running: sum, target: cars, unit: 'car', shape: 'exact' },
    }),
  ];
}

function ruleMainlineDisconnects(doc: SurveyDocument, room: MachineRoom): ValidationIssue[] {
  return disconnectSplit(
    doc,
    room,
    'V2',
    'Mainline disconnects',
    'E1a',
    'E1b',
    `Leave-as-is isn't allowed for disconnects. Replace + refurbish must equal ${room.cars.length}.`,
  );
}

function ruleCabLightDisconnects(doc: SurveyDocument, room: MachineRoom): ValidationIssue[] {
  return disconnectSplit(
    doc,
    room,
    'V3',
    'Cab light disconnects',
    'E5a',
    'E5b',
    // D-053 (Chip, 2026-10-27): "Cab light disconnects = 1 per car" — what the
    // equality below has always meant. This used to open "Every car has two
    // disconnects" (the mainline and this one), which read as two of these.
    `One cab light disconnect per car, separate from the mainline split. ` +
      `Replace + refurbish must equal ${room.cars.length}.`,
  );
}

/* ═════════════════ V4 — surge protection and relocation ceilings ══════════ */

/** §4 rule 4. Both are "≤ car count"; each gets its own issue so the chip can
 *  point at the stepper that is wrong. */
function ruleRoomCeilings(doc: SurveyDocument, room: MachineRoom): ValidationIssue[] {
  const scope: Scope = { roomId: room.id };
  const cars = room.cars.length;
  const issues: ValidationIssue[] = [];

  const surge = countOf(doc, 'E2', scope);
  if (surge > cars) {
    issues.push(
      issue({
        ruleId: 'V4',
        severity: 'error',
        scope,
        questionIds: ['E2'],
        message:
          `Mainline surge protection: ${surge} for ${pluralize(cars, 'car')} — ` +
          `that's more surge protectors than cars.`,
        explanation: 'One mainline per car, so surge protection can never exceed the car count.',
        tally: {
          subject: 'Mainline surge protection',
          running: surge,
          target: cars,
          unit: 'car',
          shape: 'ceiling',
        },
      }),
    );
  }

  const relocations = countOf(doc, 'E3', scope);
  if (relocations > cars) {
    issues.push(
      issue({
        ruleId: 'V4',
        severity: 'error',
        scope,
        questionIds: ['E3'],
        message:
          `Disconnect relocations: ${relocations} for ${pluralize(cars, 'car')} — ` +
          `that's more relocations than cars.`,
        explanation: 'You can only relocate a disconnect that exists — one per car.',
        tally: {
          subject: 'Disconnect relocations',
          running: relocations,
          target: cars,
          unit: 'car',
          shape: 'ceiling',
        },
      }),
    );
  }

  return issues;
}

/* ═══════════════════════════ V5 — the shunt flip ══════════════════════════ */

/**
 * §4 rule 5 — the ONE validation that mutates answers.
 *
 * Sprinkler in the room (M4 = Y) + shunts inside the disconnects (E4a) + some
 * disconnects marked refurbish (E1b) + those refurbish candidates have no
 * existing shunt (E4c1 = No) ⇒ they cannot be refurbished. The form flips them
 * to replace, so E1a becomes the full car count and E1b becomes zero, and tells
 * the surveyor why.
 *
 * The AutoFix payload is contract, not decoration: the form applies `set` and
 * shows `explanation` (brief §6.2). Severity is `warning` because the survey
 * corrects itself — this must never land in the gap list Chip and Tim work.
 *
 * E4c1 is the follow-up's own id. E4c is the AUTO new-with-shunt quantity and
 * carries no surveyor answer at all; testing E4c here would read a key that is
 * never written.
 */
function ruleShuntFlip(doc: SurveyDocument, room: MachineRoom): ValidationIssue[] {
  const scope: Scope = { roomId: room.id };
  if (boolOf(doc, 'M4', scope) !== true) return [];
  if (selectOf(doc, 'E4a', scope) !== 'in_disconnect') return [];

  const refurbish = countOf(doc, 'E1b', scope);
  if (refurbish <= 0) return [];

  // Only an explicit "no". An unanswered follow-up is a gap, not a flip.
  if (boolOf(doc, 'E4c1', scope) !== false) return [];

  const cars = room.cars.length;
  const flipped =
    refurbish === 1
      ? 'That 1 is now a new mainline disconnect with shunt'
      : `Those ${refurbish} are now new mainline disconnects with shunt`;
  const explanation =
    'Changed to replace. A disconnect can only be refurbished if it already has a shunt. ' +
    `${flipped} — replace count is ${cars}.`;

  const autoFix: AutoFix = {
    kind: 'shunt_refurb_to_replace',
    scope,
    set: [
      { questionId: 'E1a', value: { kind: 'flagCount', value: { flag: cars > 0, count: cars } } },
      { questionId: 'E1b', value: { kind: 'flagCount', value: { flag: false, count: 0 } } },
    ],
    explanation,
  };

  return [
    issue({
      ruleId: 'V5',
      severity: 'warning',
      scope,
      questionIds: ['E1a', 'E1b', 'E4c1'],
      message: explanation,
      autoFix,
    }),
  ];
}

/**
 * Apply an auto-fix to a document. Pure — returns a new document and never
 * touches the original, and deliberately does NOT stamp `updatedAt`: this module
 * takes no clock, so the caller that owns the clock stamps it.
 */
export function applyAutoFix(doc: SurveyDocument, fix: AutoFix): SurveyDocument {
  const answers = { ...doc.answers };
  for (const op of fix.set) {
    const question = byId(op.questionId);
    const scope = question ? questionScope(question, fix.scope) : fix.scope;
    answers[answerKey(op.questionId, scope)] = op.value;
  }
  return { ...doc, answers };
}

/* ═════════════════════════ V6 — pit ladder ceiling ════════════════════════ */

/**
 * §4 rule 6. Replace + modify ≤ cars in THIS hoistway. Not the equality rule the
 * disconnects use: a ladder that already meets code is left alone, so a
 * remainder is the normal outcome. Only an excess is wrong.
 */
function ruleLadders(doc: SurveyDocument, room: MachineRoom): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  for (const hoistway of room.hoistways) {
    const scope: Scope = { roomId: room.id, hoistwayId: hoistway.id };
    const cars = hoistway.carIds.length;
    const replace = countOf(doc, 'P9a', scope);
    const modify = countOf(doc, 'P9b', scope);
    const sum = replace + modify;
    if (sum <= cars) continue;

    issues.push(
      issue({
        ruleId: 'V6',
        severity: 'error',
        scope,
        questionIds: ['P9a', 'P9b'],
        message:
          `Pit ladders: ${replace} + ${modify} = ${sum} for ${pluralize(cars, 'car')} in this ` +
          `hoistway — that's more ladders than cars.`,
        explanation:
          'Replace + modify can be less than the car count — leftover ladders are ' +
          'code-compliant and stay as they are. It can never be more.',
        tally: {
          subject: 'Pit ladders',
          running: sum,
          target: cars,
          unit: 'car',
          shape: 'ceiling',
        },
      }),
    );
  }
  return issues;
}

/* ═════════════════ V7 — every car placed exactly once ═════════════════════ */

/**
 * §4 rule 7. One group, one hoistway, per car — no orphans, no duplicates.
 * Reads the entity tree only: group and hoistway membership are structure
 * (M6/M7), never answers, so this rule never touches the answer map.
 */
function ruleCarAssignment(room: MachineRoom): ValidationIssue[] {
  const scope: Scope = { roomId: room.id };
  const issues: ValidationIssue[] = [];
  const knownCarIds = new Set(room.cars.map((c) => c.id));

  const dimensions = [
    { questionId: 'M6', noun: 'group', plural: 'groups', buckets: room.groups },
    { questionId: 'M7', noun: 'hoistway', plural: 'hoistways', buckets: room.hoistways },
  ] as const;

  for (const dimension of dimensions) {
    for (const car of room.cars) {
      const owners = dimension.buckets.filter((b) => b.carIds.includes(car.id));
      if (owners.length === 1) continue;

      const message =
        owners.length === 0
          ? `${car.label} isn't in a ${dimension.noun} yet.`
          : `${car.label} is in ${owners.length} ${dimension.plural} ` +
            `(${owners.map((o) => o.name).join(', ')}) — a car can only be in one.`;

      issues.push({
        ruleId: 'V7',
        severity: 'error',
        scope,
        questionIds: [dimension.questionId],
        message,
        explanation:
          owners.length === 0
            ? `Every car this room serves needs a ${dimension.noun}.`
            : `Tap the car out of the ${dimension.plural} it does not belong to.`,
      });
    }

    for (const bucket of dimension.buckets) {
      for (const carId of bucket.carIds) {
        if (knownCarIds.has(carId)) continue;
        issues.push({
          ruleId: 'V7',
          severity: 'error',
          scope,
          questionIds: [dimension.questionId],
          message:
            `${bucket.name} lists "${carId}", which isn't one of this room's cars.`,
          explanation: 'A car can only be assigned inside the room that serves it.',
        });
      }
    }
  }

  return issues;
}

/* ═════════════ V8 — recommended photos: warn, list, never block ═══════════ */

/** A recommended shot the surveyor has not covered. */
export interface MissingShot {
  sectionInstanceId: string;
  /** Room-qualified — a gap travels away from the screen that gave it context. */
  sectionTitle: string;
  shotListId: string;
  shotLabel: string;
  /** A FLAG only (Amendment 8, D-041) — copied from RecommendedShot.required. */
  required?: true;
}

export function sectionTitleOf(doc: SurveyDocument, section: SectionInstance): string {
  const room = roomOf(doc, section.scope);
  return room ? qualifyTitle(room.name, section.title) : section.title;
}

/**
 * §4 rule 8 / §2.4. Every recommended shot with no photo tagged to it.
 *
 * Conditional shots are prompted only when their predicate holds — a condensate
 * route on a chilled-water job does not exist, and there is no staging area
 * without a crane. A shot marked conditional with no registered predicate is
 * treated as not prompted rather than silently demanded; a contract test asserts
 * the predicate table stays complete.
 *
 * A photo counts as taken whatever its upload state. A `failed` upload is the
 * sync engine's problem, not a missing shot — the surveyor stood there and took
 * the picture.
 */
export function missingShots(doc: SurveyDocument): MissingShot[] {
  const missing: MissingShot[] = [];
  for (const section of buildSurveySections(doc)) {
    for (const shot of section.shotList) {
      if (shot.conditional) {
        // A per-car shot is never conditional (Amendment 8 introduces no such
        // shot), but the lookup is keyed by the shot's BASE id regardless.
        const predicate = SHOT_CONDITIONS[shotBaseId(shot.id)];
        if (!predicate || !conditionHolds(predicate, doc, section.scope)) continue;
      }
      // `photoCoversShot` is the ONE definition of "taken" — it also honours a
      // shot's former address (Amendment 6), so a record that photographed the
      // panel on the building screen is not told it did not.
      const covered = doc.photos.some((p) => photoCoversShot(p, section, shot));
      if (covered) continue;
      missing.push({
        sectionInstanceId: section.id,
        sectionTitle: sectionTitleOf(doc, section),
        shotListId: shot.id,
        shotLabel: shot.label,
        ...(shot.required ? { required: true } : {}),
      });
    }
  }
  return missing;
}

/** The `missingPhotos` list carried on a SubmittedSurvey (§4 rule 8). */
export function missingPhotos(doc: SurveyDocument): SubmittedSurvey['missingPhotos'] {
  return missingShots(doc).map((m) => ({
    sectionInstanceId: m.sectionInstanceId,
    sectionTitle: m.sectionTitle,
    shotLabel: m.shotLabel,
    ...(m.required ? { required: true } : {}),
  }));
}

/**
 * How an uncovered recommended shot READS — Amendment 5, and a correction.
 *
 * It used to say *"Recommended photo missing: Controller (…)"*, and the design
 * authority read it back to us as *"it flagged an error at the end saying that
 * there is a missing controller"*. It was never an error — §4 rule 8 is
 * warn-don't-block, this is a `warning`, and `missing_photo` is deliberately not
 * in `BLOCKING_GAP_REASONS` — but "missing", capitalised and leading, is the
 * word the surveyor was actually shown, and a person cannot be expected to read
 * a severity field.
 *
 * So the sentence says what is true and nothing more: there is no photo of this
 * yet, and nothing is wrong. It is one string, printed on the section close
 * panel, in the last look, in the gap on the record and in the office's
 * notification, so all four now agree that this is a note and not a fault.
 */
function missingPhotoMessage(shot: MissingShot): string {
  return shot.required
    ? `No photo yet of ${shot.shotLabel} (${shot.sectionTitle}) — required.`
    : `No photo yet of ${shot.shotLabel} (${shot.sectionTitle}) — recommended, not required.`;
}

/**
 * V8's `explanation`, alongside `missingPhotoMessage` (D-041 postscript).
 *
 * Every V8 issue used to carry the same "recommended, not required" sentence
 * regardless of the shot — true for the message, false for a REQUIRED shot's
 * explanation, and this rides on `SubmittedSurvey.validation[]` where the
 * office reads it too. A required shot's explanation says what actually
 * closes it: the photo, or the surveyor's written reason on that photo's
 * card (§5, D-041) — never "you can always close the section", which is
 * exactly what a required shot with nothing written against it cannot do.
 */
function missingPhotoExplanation(shot: MissingShot): string {
  return shot.required
    ? "This photo is required. Take it, or write why it could not be taken on the photo's card; the office resolves it."
    : 'Photos are recommended, not required — you can always close the section. ' +
        'Missing shots are listed on the submitted survey.';
}

function rulePhotos(doc: SurveyDocument): ValidationIssue[] {
  // Build the walk once, not once per missing shot.
  const scopes = new Map(buildSurveySections(doc).map((s) => [s.id, s.scope]));
  return missingShots(doc).map((shot) =>
    issue({
      ruleId: 'V8',
      severity: 'warning',
      scope: scopes.get(shot.sectionInstanceId) ?? {},
      questionIds: [],
      message: missingPhotoMessage(shot),
      explanation: missingPhotoExplanation(shot),
    }),
  );
}

/* ═══════════════════════ V9 — consultant bid due date ═════════════════════ */

/** §4 rule 9. A consultant bid is bid to a deadline. */
function ruleBidDueDate(doc: SurveyDocument): ValidationIssue[] {
  if (boolOf(doc, 'B8', {}) !== true) return [];
  if (isAnswered(answerOf(doc, 'B10', {}))) return [];
  return [
    issue({
      ruleId: 'V9',
      severity: 'error',
      scope: {},
      questionIds: ['B10'],
      message: 'This is a consultant bid, so it needs a bid due date.',
      explanation: 'Consultant jobs are bid to a deadline — the office cannot schedule without it.',
    }),
  ];
}

/* ═════════ V11 — a ZIP is all five digits before it counts (§4 rule 12, D-051) ═════════ */

/**
 * VA Tuskegee, 2026-09-18: ZIP was Chip's last open item on the last look. His
 * first keystroke, "3", counted as an answer, the list closed under his
 * finger, and the survey would have gone out with a one-digit ZIP. A partial
 * ZIP is a gap, not an answer — the row stays open until all five digits are
 * there. Blank stays rule 11's business (unanswered), so this never doubles
 * that report. ZIP+4 is accepted; the office files by the first five.
 */
const ZIP_PATTERN = /^\d{5}(?:-\d{4})?$/;

function ruleZip(doc: SurveyDocument): ValidationIssue[] {
  const answer = answerOf(doc, 'B2c', {});
  if (answer?.kind !== 'text') return [];
  const typed = answer.value.trim();
  if (typed === '' || ZIP_PATTERN.test(typed)) return [];
  return [
    issue({
      ruleId: 'V11',
      severity: 'error',
      scope: {},
      questionIds: ['B2c'],
      message: 'ZIP — needs all five digits.',
      explanation: 'The office files the job by its ZIP; a partial one cannot be looked up.',
    }),
  ];
}

/* ═════════════ V10 — chilled water forces a manual-price line ═════════════ */

/**
 * §4 rule 10. Chilled water is priced by hand at the office, so the estimate
 * skeleton must carry a manual line that cannot be quietly dropped. This issue
 * IS that marker — it is `info`, it never clears, and it is not a gap.
 */
function ruleChilledWater(doc: SurveyDocument, room: MachineRoom): ValidationIssue[] {
  const scope: Scope = { roomId: room.id };
  const question = byId('H1a');
  if (!question || !isVisible(question, doc, scope)) return [];
  if (selectOf(doc, 'H1a', scope) !== 'chilled') return [];

  return [
    issue({
      ruleId: 'V10',
      severity: 'info',
      scope,
      questionIds: ['H1a', 'H4'],
      message:
        "Chilled water — the office prices this by hand. This line can't be dropped from " +
        'the estimate.',
      explanation:
        'Write down what you see and photograph it well. The estimate carries a ' +
        'manual-price line for this either way.',
    }),
  ];
}

/**
 * §4 rule 10, second line — the FLS subcontractor proposal (F2), Amendment 5.
 *
 * Same rule and same mechanism as chilled water, which is why it carries the
 * same `ruleId`: rule 10 is *"this line is priced by hand and cannot be dropped
 * from the estimate skeleton"*, and the `info` issue IS the line's marker. The
 * only difference is the gate, and F2 has none — **every** job on this scope of
 * work carries an FLS subcontractor line, so this fires on every survey, from
 * the empty one forward.
 *
 * Building-scoped and emitted once. A second copy per machine room would read as
 * two proposals from one subcontractor.
 */
function ruleFlsProposal(): ValidationIssue[] {
  return [
    issue({
      ruleId: 'V10',
      severity: 'info',
      scope: {},
      questionIds: ['F2'],
      message:
        'Fire Life Safety subcontractor — the office prices this by hand. ' +
        "This line can't be dropped from the estimate.",
      explanation:
        'Every job carries an FLS line. Photograph the panel and its sticker, and write ' +
        'down anything that would change their number — the estimate carries the line either way.',
    }),
  ];
}

/* ═══════════════════════════ the engine ═══════════════════════════════════ */

/**
 * Every specification §4 issue for a document, in a stable order: building
 * rules, then each room in document order, then photos. Deterministic — no
 * clock, no randomness, no dependence on object key order.
 */
export function validateSurvey(doc: SurveyDocument): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  issues.push(...ruleUnitTotal(doc));
  issues.push(...ruleBidDueDate(doc));
  issues.push(...ruleZip(doc));
  issues.push(...ruleFlsProposal());

  for (const room of doc.rooms) {
    issues.push(...ruleMainlineDisconnects(doc, room));
    issues.push(...ruleCabLightDisconnects(doc, room));
    issues.push(...ruleRoomCeilings(doc, room));
    issues.push(...ruleShuntFlip(doc, room));
    issues.push(...ruleLadders(doc, room));
    issues.push(...ruleCarAssignment(room));
    issues.push(...ruleChilledWater(doc, room));
  }

  issues.push(...rulePhotos(doc));

  return issues;
}

export { validateSurvey as validate };

/* ══════════════════════════════ gaps (§4 rule 11) ═════════════════════════ */

/**
 * The reasons that actually stop a survey at Chip and Tim.
 *
 * `missing_photo` is deliberately absent. §4 rule 8 says missing RECOMMENDED
 * photos are warned on section close and *listed* on the submitted survey and
 * "never block". `missing_required_photo` IS present: Chip ruled (2026-09-06)
 * that three photos hold the quote — the FACP nameplate, the controller, the
 * full pit — so an uncovered required shot blocks exactly like an unanswered
 * required answer, and the surveyor's written reason for skipping it travels
 * as the gap's `surveyorNote` the same way an acknowledgement does.
 */
export const BLOCKING_GAP_REASONS: readonly GapReason[] = [
  'unanswered',
  'validation_failed',
  'missing_required_photo',
  'surveyor_flagged',
];

/** The note a surveyor wrote against a shot they could not take (Amendment 8). */
export function shotNoteFor(
  doc: SurveyDocument,
  sectionInstanceId: string,
  shotListId: string,
): Note | undefined {
  return doc.notes.find(
    (note) => note.sectionInstanceId === sectionInstanceId && note.shotListId === shotListId,
  );
}

/**
 * `shotNoteFor`'s TEXT, trimmed, and only when there is any left (D-041
 * postscript).
 *
 * The reason field upserts on every keystroke and never deletes the note it
 * wrote (`ShotReasonField` — a typed-then-cleared reason is a real note with
 * `text: ''`), so every reader of a shot's written reason has to make the same
 * choice `shotNoteFor`'s own callers used to make separately: empty (or
 * whitespace-only) text reads as no reason at all, not as `reason: ''` riding
 * onto a gap's `surveyorNote` or the cut sheet's `open[].reason` — both of
 * which the schema itself refuses (`GapSchema` has no such field to violate,
 * but `CutSheetOpenItemSchema.reason` is `z.string().min(1)`). One function,
 * so `detectGapsWithIssues`, `photoShelf.ts` and `flsCutSheet.ts` read the
 * same undefined the same way.
 */
export function shotReasonFor(
  doc: SurveyDocument,
  sectionInstanceId: string,
  shotListId: string,
): string | undefined {
  const trimmed = shotNoteFor(doc, sectionInstanceId, shotListId)?.text.trim();
  return trimmed ? trimmed : undefined;
}

function gapId(sectionId: string, reason: GapReason, key: string): string {
  return `${sectionId}#${reason}#${key}`;
}

/** The section a validation issue belongs to, for the gap it becomes. */
function sectionIdForIssue(issue_: ValidationIssue): string {
  for (const questionId of issue_.questionIds) {
    const question = byId(questionId);
    if (!question) continue;
    return sectionInstanceId(question.section, questionScope(question, issue_.scope));
  }
  return sectionInstanceId('building');
}

/**
 * Everything outstanding on a survey: unanswered visible required questions,
 * failed validations, and missing recommended photos — each carrying the
 * room-qualified section title so a gap reads on its own in the submission
 * notification (spec §2.3).
 *
 * Fully-derived and structural question ids are never gaps (see module header).
 * A question that is both unanswered AND failing a rule is reported once, as
 * unanswered: "answer it" is the actionable instruction, and two lines for one
 * empty stepper is noise in a list Chip and Tim work by hand.
 */
/**
 * `detectGaps`, plus the §4 issue each `validation_failed` gap came from.
 *
 * ── Why provenance has to be emitted here and not reconstructed ─────────────
 * A `Gap` deliberately carries one `questionId`, one sentence, and nothing else:
 * it is the office's record, written to be readable in a Telegram message in a
 * truck. But a §4 arithmetic rule spans TWO questions (replace + refurbish) and
 * has a target and a running total, and a surveyor who is being refused needs
 * all of that — *which two steppers, and what they have to add up to*. That
 * information exists, in `ValidationIssue`, for exactly as long as this function
 * runs and then is thrown away.
 *
 * Rebuilding the pairing afterwards means re-deriving which section an issue
 * belongs to and re-deriving the suppression rule below, in a second place that
 * can drift from this one. So the map is handed out instead. `detectGaps` is
 * unchanged and still returns byte-identical gaps.
 */
export interface DetectedGaps {
  gaps: Gap[];
  /** gap id → the issue behind it. Only `validation_failed` gaps have one. */
  issueByGapId: Map<string, ValidationIssue>;
}

export function detectGaps(doc: SurveyDocument): Gap[] {
  return detectGapsWithIssues(doc).gaps;
}

export function detectGapsWithIssues(doc: SurveyDocument): DetectedGaps {
  const sections = buildSurveySections(doc);
  const issueByGapId = new Map<string, ValidationIssue>();
  const titles = new Map(sections.map((s) => [s.id, sectionTitleOf(doc, s)]));
  const gaps: Gap[] = [];
  const claimed = new Set<string>();

  /* 1. Unanswered visible required questions (§4 rule 11). */
  for (const section of sections) {
    const room = roomOf(doc, section.scope);
    const formSet = room ? formSetFor(room.type) : undefined;
    for (const question of questionsFor(section.kind, formSet)) {
      if (!question.required) continue;
      if (STRUCTURAL_QUESTION_IDS.includes(question.id)) continue;
      if (DERIVED_ONLY_QUESTION_IDS.includes(question.id)) continue;
      if (!isVisible(question, doc, section.scope)) continue;

      const stored = doc.answers[answerKey(question.id, questionScope(question, section.scope))];
      if (isAnsweredFor(question, stored)) continue;

      claimed.add(`${section.id}|${question.id}`);
      gaps.push({
        id: gapId(section.id, 'unanswered', question.id),
        sectionInstanceId: section.id,
        sectionTitle: titles.get(section.id) ?? section.title,
        questionId: question.id,
        reason: 'unanswered',
        // "Not answered" would be a lie about the one incomplete state that
        // LOOKS answered: a flagCount left on Yes with no quantity. The office
        // is told what is actually missing, in the surveyor's own words.
        // A ZIP with some digits in it is the other one (D-051): typed, not
        // yet an answer — say what it needs, not that it was skipped.
        message:
          stored?.kind === 'flagCount' && stored.value.flag
            ? `${question.label} — answered Yes, but the quantity is still 0.`
            : isAnswered(stored)
              ? `${question.label} — needs all five digits.`
              : `${question.label} — not answered.`,
      });
    }
  }

  /* 2. Failed validations. Only `error` blocks; the shunt flip corrects itself
   *    and missing photos are handled below with their own reason.
   *
   *    A question already reported as unanswered is NOT reported again here —
   *    "answer it" is the one actionable instruction, and two lines for one
   *    empty stepper is noise in a list Chip and Tim work by hand. That is the
   *    only suppression: two DIFFERENT findings on the same question are two
   *    different facts. Rule 7 is the case that matters — every unassigned car
   *    in a room is its own V7 issue against M6, and dropping all but the first
   *    would send the office chasing Car 3 while Car 4 is still homeless. Their
   *    gap ids would collide, so repeats are numbered; `validateSurvey` is
   *    deterministic, so the numbering is too. */
  const occurrences = new Map<string, number>();
  for (const found of validateSurvey(doc)) {
    if (found.severity !== 'error') continue;
    const sectionId = sectionIdForIssue(found);
    const questionId = found.questionIds[0];
    if (questionId && claimed.has(`${sectionId}|${questionId}`)) continue;

    const base = `${found.ruleId}${questionId ? `:${questionId}` : ''}`;
    const seen = occurrences.get(`${sectionId}|${base}`) ?? 0;
    occurrences.set(`${sectionId}|${base}`, seen + 1);

    const id = gapId(sectionId, 'validation_failed', seen === 0 ? base : `${base}#${seen + 1}`);
    issueByGapId.set(id, found);
    gaps.push({
      id,
      sectionInstanceId: sectionId,
      sectionTitle: titles.get(sectionId) ?? sectionId,
      ...(questionId ? { questionId } : {}),
      reason: 'validation_failed',
      message: found.message,
    });
  }

  /* 3. Photos. A recommended shot is listed, never blocking (§4 rule 8). A
   *    REQUIRED shot (Amendment 8, D-041) blocks like an unanswered question;
   *    the surveyor's written reason, a note addressed to the shot, travels as
   *    the gap's own `surveyorNote` — it does not remove the gap (§2.3). */
  for (const shot of missingShots(doc)) {
    const reason: GapReason = shot.required ? 'missing_required_photo' : 'missing_photo';
    const note = shot.required
      ? shotReasonFor(doc, shot.sectionInstanceId, shot.shotListId)
      : undefined;
    gaps.push({
      id: gapId(shot.sectionInstanceId, reason, shot.shotListId),
      sectionInstanceId: shot.sectionInstanceId,
      sectionTitle: shot.sectionTitle,
      reason,
      message: missingPhotoMessage(shot),
      ...(note ? { surveyorNote: note } : {}),
    });
  }

  return { gaps, issueByGapId };
}

/**
 * The submitted status a gap list implies — the ONE statement of that rule.
 *
 * ── Why this is exported, and what it fixed ─────────────────────────────────
 * "Which status goes with these gaps?" is answered in three places: here, on
 * the device at submit, and at the API, which refuses a submission whose status
 * and gap list disagree ("the office queue would lie"). The API stated the rule
 * for itself, as `gaps.length > 0 ⇒ submitted_with_gaps` — and that is a
 * DIFFERENT rule from this one, because `missing_photo` is deliberately not a
 * blocking reason (§4 rule 8: photos never block).
 *
 * The two agreed for as long as no document ever carried a non-blocking gap.
 * The moment one did — a survey finished properly with one recommended photo
 * not taken, which is an ordinary Tuesday — the device wrote `submitted_clean`
 * with a `missing_photo` gap on the record, exactly as §4 rule 8 requires, and
 * the office answered **422** and refused the submission. The surveyor's day of
 * work then sat parked on the phone under "the office could not accept this",
 * for the one reason the specification says must never block.
 *
 * Found by the E2E walk on 2026-08-07, once Amendment 5 gave the building
 * section two recommended shots that walk could not satisfy. It was not caused
 * by Amendment 5 — it was reachable in the field the day §4 rule 8 shipped.
 *
 * So the rule is stated once, here, next to `BLOCKING_GAP_REASONS`, and both
 * the device and the API read it rather than restating it.
 */
export function statusForGapList(gaps: readonly Gap[]): SurveyStatus {
  return gaps.some((gap) => BLOCKING_GAP_REASONS.includes(gap.reason))
    ? 'submitted_with_gaps'
    : 'submitted_clean';
}

/**
 * `submitted_clean` vs `submitted_with_gaps` (brief §5), for the gaps the
 * engine detects over `doc` as it stands.
 *
 * Only those. `doc.gaps` is the record of what was last SENT and is read by
 * the office with `statusForGapList`; it is not a second source of live gaps.
 * It used to be merged in here so a surveyor-flagged locked pit — written to
 * `doc.gaps` by a card the phone no longer has (D-033 R15) — could keep the
 * survey gapped. That merge is what let a rule the engine no longer failed
 * stay on a reopened record (round 8, task 1). A pre-D-033 flag on a record
 * is history the office already holds, not a live gap.
 */
export function surveyStatusFor(
  doc: SurveyDocument,
  gaps: Gap[] = detectGaps(doc),
): SurveyStatus {
  return statusForGapList(gaps);
}
